import React, { useEffect, useState } from 'react';
import { Book, BookNote } from '../objects/Book';
import { listNotes, createNote, updateNote } from '../repository/readingRepository';

interface BookNotesProps {
  book: Book;
  onBack: () => void;
}

const formatDate = (isoDate?: string): string => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/New_York',
  });
};

const BookNotes: React.FC<BookNotesProps> = ({ book, onBack }) => {
  const [notes, setNotes] = useState<BookNote[]>([]);
  const [editingNote, setEditingNote] = useState<BookNote | null>(null);
  const [content, setContent] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchNotes = async () => {
      const result = await listNotes(book.uuid);
      if (isMounted) {
        setNotes(result);
      }
    };
    fetchNotes();
    return () => {
      isMounted = false;
    };
  }, [book.uuid]);

  const handleSaveNew = async () => {
    setIsSaving(true);
    setMessage(null);
    try {
      const saved = await createNote(book.uuid, content);
      setNotes([saved, ...notes]);
      setContent('');
      setIsComposing(false);
      setMessage('Note added.');
    } catch {
      setMessage('Failed to save note.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingNote) return;
    setIsSaving(true);
    setMessage(null);
    try {
      const saved = await updateNote(editingNote.uuid, content);
      setNotes(notes.map((n) => (n.uuid === saved.uuid ? saved : n)));
      setEditingNote(null);
      setContent('');
      setMessage('Note updated.');
    } catch {
      setMessage('Failed to update note.');
    } finally {
      setIsSaving(false);
    }
  };

  const startEditing = (note: BookNote) => {
    setEditingNote(note);
    setContent(note.content);
    setIsComposing(false);
  };

  const startComposing = () => {
    setIsComposing(true);
    setEditingNote(null);
    setContent('');
  };

  const cancelEdit = () => {
    setEditingNote(null);
    setIsComposing(false);
    setContent('');
    setMessage(null);
  };

  const isEditing = isComposing || editingNote !== null;

  return (
    <div className="card">
      <div className="card-section">
        <div className="notes-header">
          <div>
            <h2>{book.title}</h2>
            <p className="helper-text">{book.author ? `by ${book.author}` : 'Author unknown'}</p>
          </div>
          <button className="button secondary" onClick={onBack}>
            Back to list
          </button>
        </div>

        {isEditing && (
          <div style={{ marginTop: '1rem' }}>
            <p className="helper-text">
              {editingNote ? 'Edit note:' : 'New note:'}
            </p>
            <div className="field" style={{ marginTop: '0.5rem' }}>
              <textarea
                className="textarea"
                rows={6}
                value={content}
                onChange={(event) => setContent(event.target.value)}
                placeholder="Write your note..."
              />
            </div>
            <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <button
                className="button"
                onClick={editingNote ? handleUpdate : handleSaveNew}
                disabled={isSaving || content.trim().length === 0}
              >
                {isSaving ? 'Saving...' : editingNote ? 'Update note' : 'Add note'}
              </button>
              <button className="button secondary" onClick={cancelEdit}>
                Cancel
              </button>
              {message && <span className="helper-text">{message}</span>}
            </div>
          </div>
        )}

        {!isEditing && (
          <div style={{ marginTop: '1rem' }}>
            <button className="button" onClick={startComposing}>
              Add a note
            </button>
            {message && <span className="helper-text" style={{ marginLeft: '0.75rem' }}>{message}</span>}
          </div>
        )}

        {notes.length > 0 && (
          <div style={{ marginTop: '1.5rem' }}>
            {notes.map((note) => (
              <div
                key={note.uuid}
                style={{
                  background: '#f8fafc',
                  padding: '0.75rem',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '0.75rem',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <span className="helper-text">
                    {formatDate(note.created_at)}
                    {note.updated_at && note.updated_at !== note.created_at && ` (edited ${formatDate(note.updated_at)})`}
                  </span>
                  <button
                    className="button small secondary"
                    onClick={() => startEditing(note)}
                    style={{ flexShrink: 0 }}
                  >
                    Edit
                  </button>
                </div>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 'var(--font-size-sm)', color: 'var(--color-text)' }}>
                  {note.content}
                </div>
              </div>
            ))}
          </div>
        )}

        {notes.length === 0 && !isEditing && (
          <p className="helper-text" style={{ marginTop: '1rem' }}>No notes yet.</p>
        )}
      </div>
    </div>
  );
};

export default BookNotes;
